import React from 'react';
import { BasicResourceConfigurationFormProps } from '../../../types';
import { useTranslation } from '../../../contexts/TranslationContext';
import { RichTextEditor } from '../../Common/RichTextEditor';

export const RichTextConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config,
  onUpdate,
}) => {
  const { t } = useTranslation();
  
  const currentContent = config?.content || config?.html || config?.text || resource.content || resource.html || resource.text || '';
  const currentTitle = config?.title || config?.caption || resource.title || resource.caption || '';
  const currentAlignment = config?.alignment || config?.align || resource.alignment || resource.align || 'left';
  const currentFontSize = config?.fontSize || config?.font_size || resource.fontSize || resource.font_size || 'inherit';
  const currentBackgroundColor = config?.backgroundColor || config?.bgColor || resource.backgroundColor || resource.bgColor || 'white';
  const currentTextColor = config?.textColor || config?.color || resource.textColor || resource.color || 'inherit';
  const currentMaxWidth = config?.maxWidth || config?.max_width || resource.maxWidth || resource.max_width || '';
  const currentPadding = config?.padding || config?.p || resource.padding || resource.p || '1rem';

  const handleContentChange = (content: string) => {
    onUpdate({ content, html: content, text: content });
  };

  const handleTitleChange = (title: string) => {
    onUpdate({ title, caption: title });
  };

  return (
    <div className="space-y-4">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('richTextConfig.title')}
        </label>
        <input
          type="text"
          value={currentTitle}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder={t('richTextConfig.titlePlaceholder')}
          className="input-field"
        />
        <p className="text-xs text-gray-500 mt-1">{t('richTextConfig.titleHint')}</p>
      </div>

      {/* Content Editor */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('richTextConfig.content')}
        </label>
        <div className="border border-gray-300 rounded-lg overflow-hidden">
          <RichTextEditor
            value={currentContent}
            onChange={handleContentChange}
            placeholder={t('richTextConfig.contentPlaceholder')}
            rows={8}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">{t('richTextConfig.contentHint')}</p>
      </div>

      {/* Preview */}
      {currentContent && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h4 className="text-sm font-medium text-gray-700 mb-2">{t('richTextConfig.preview')}</h4>
          <div
            className="rich-text-content bg-white p-3 rounded border border-gray-200"
            dangerouslySetInnerHTML={{ __html: currentContent }}
          />
        </div>
      )}

      {/* Alignment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('richTextConfig.alignment')}
        </label>
        <select
          value={currentAlignment}
          onChange={(e) => onUpdate({ alignment: e.target.value, align: e.target.value })}
          className="input-field"
        >
          <option value="left">{t('richTextConfig.left')}</option>
          <option value="center">{t('richTextConfig.center')}</option>
          <option value="right">{t('richTextConfig.right')}</option>
          <option value="justify">{t('richTextConfig.justify')}</option>
        </select>
      </div>

      {/* Font Size */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('richTextConfig.fontSize')}
        </label>
        <select
          value={currentFontSize}
          onChange={(e) => onUpdate({ fontSize: e.target.value, font_size: e.target.value })}
          className="input-field"
        >
          <option value="inherit">{t('richTextConfig.inherit')}</option>
          <option value="12px">12px</option>
          <option value="14px">14px</option>
          <option value="16px">16px</option>
          <option value="18px">18px</option>
          <option value="20px">20px</option>
          <option value="24px">24px</option>
        </select>
      </div>

      {/* Colors */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('richTextConfig.backgroundColor')}
          </label>
          <input
            type="color"
            value={currentBackgroundColor === 'white' ? '#ffffff' : currentBackgroundColor}
            onChange={(e) => onUpdate({ backgroundColor: e.target.value, bgColor: e.target.value })}
            className="input-field h-10"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('richTextConfig.textColor')}
          </label>
          <input
            type="color"
            value={currentTextColor === 'inherit' ? '#000000' : currentTextColor}
            onChange={(e) => onUpdate({ textColor: e.target.value, color: e.target.value })}
            className="input-field h-10"
          />
        </div>
      </div>

      {/* Max Width */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('richTextConfig.maxWidth')}
        </label>
        <input
          type="text"
          value={currentMaxWidth}
          onChange={(e) => onUpdate({ maxWidth: e.target.value, max_width: e.target.value })}
          placeholder="none, 800px, 100%"
          className="input-field"
        />
        <p className="text-xs text-gray-500 mt-1">{t('richTextConfig.maxWidthHint')}</p>
      </div>

      {/* Padding */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('richTextConfig.padding')}
        </label>
        <input
          type="text"
          value={currentPadding}
          onChange={(e) => onUpdate({ padding: e.target.value, p: e.target.value })}
          placeholder="1rem, 16px, 1rem 2rem"
          className="input-field"
        />
        <p className="text-xs text-gray-500 mt-1">{t('richTextConfig.paddingHint')}</p>
      </div>
    </div>
  );
};

